// src/features/auth/pages/ChangePasswordPage.tsx
import { useState } from 'react';
import { Form, Input, Button, Typography, Alert } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { changePassword } from '../../api/apiEndpoints';
import type { ChangePasswordInputs } from '../../types/managementTypes';
import validationPatterns from '@/common/components/DynamicForm/ValidationPatterns';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

function ChangePasswordPage() {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const [changeSuccess, setChangeSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);


    const mutation = useMutation({
        mutationFn: (data: ChangePasswordInputs) => changePassword(data),
        onSuccess: (response: any) => {

            if (response?.isError) {
                setErrorMessage(response.message || t("Something went wrong."));
                setChangeSuccess(false);
            } else {
                setChangeSuccess(true);
                setErrorMessage(null);
            }
        },
        onError: (error: any) => {
            console.error("Change password error:", error);
            setErrorMessage(error.message || t("Unexpected error occurred."));
            setChangeSuccess(false);
        },
    });

    const onFinish = (values: ChangePasswordInputs) => {
        mutation.mutate(values);
    };

    return (
        <div style={{ maxWidth: 400, margin: '0 auto', padding: '40px 20px' }}>
            <Title level={3} style={{ marginBottom: '24px', textAlign: 'center' }}>
                {t("Change Your Password")}
            </Title>

            {changeSuccess ? (
                <Alert
                    message={t("Password Changed Successfully")}
                    description={
                        t("Your password has been successfully changed. You may logout and re-login with your new password.")
                    }
                    type="success"
                    showIcon
                />
            ) : (
                <>
                    {errorMessage && (
                        <Alert
                            message={t("Password Change Failed")}
                            description={errorMessage}
                            type="error"
                            showIcon
                            style={{ marginBottom: "16px" }}
                        />
                    )}

                    <Text type="secondary" style={{ marginBottom: "16px", display: "block" }}>
                        {t("Please enter your current password and your new password below.")}
                    </Text>

                    <Form form={form} name="change_password_form" onFinish={onFinish} layout="vertical">
                        <Form.Item
                            name="oldPassword"
                            rules={[{ required: true, message: t("Please input your current password!") }]}
                            hasFeedback
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder={t("Current Password")} />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[
                                { required: true, message: t("Please input your new password!") },
                                {
                                    pattern: new RegExp(validationPatterns.PasswordValidateExp.regex),
                                    message: validationPatterns.PasswordValidateExp.message,
                                },
                            ]}
                            hasFeedback
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder={t("New Password")} />
                        </Form.Item>

                        <Form.Item
                            name="confirmPassword"
                            dependencies={["password"]}
                            hasFeedback
                            rules={[
                                { required: true, message: t("Please confirm your new password!") },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue("password") === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t("The two passwords that you entered do not match!")));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder={t("Confirm New Password")} />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={mutation.isPending} block>
                                {t("Change Password")}
                            </Button>
                        </Form.Item>
                    </Form>
                </>
            )}

        </div>
    );
}

export default ChangePasswordPage;
